import React, { useContext, useState } from 'react';
import { StatusViewsContext } from '../StatusViews.context';
import {
  Actions,
  BigIcon,
  CenteredContainer,
  Description,
  Title,
  Wrapper,
} from '../StatusViews.styles';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { useDraftAccountQuery } from '../../../ApexOPL/AccountOpening/api/useDraftAccountQuery';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { draftAccountToFormFields } from '../../ApexHelpers';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { YES_NO_OPTIONS } from '../../ApexConstants';
import { Field, Label, Value } from './Review.styles';
import { Card } from '../../../CommonComponents/OPL/Card/Card';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { useResetModuleWrapperStyles } from '../../../_hooks/useResetModuleWrapperStyles';
import { Content, StickyFooter } from '../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Letter407 } from './components/Letter407/Letter407';
import { ConfirmationModal } from './components/ConfirmationModal/ConfirmationModal';

interface Props {
  setStatusView: (view: string) => void;
}

export const Review = ({ setStatusView }: Props) => {
  const [isReviewVisible, setIsReviewVisible] = useState(false);

  if (isReviewVisible) {
    return <ReviewContent setStatusView={setStatusView} />;
  }

  return (
    <Wrapper>
      <CenteredContainer>
        <BigIcon className="far fa-history" />

        <Title fontSize="h4" color="neutral">
          Advisor review and approval required
        </Title>

        <Description>Please approve the application to open the account.</Description>

        <Actions>
          <Button onClick={() => setIsReviewVisible(true)}>Review Application</Button>
        </Actions>
      </CenteredContainer>
    </Wrapper>
  );
};

interface ContentProps {
  setStatusView: (view: string) => void;
}

const ReviewContent = ({ setStatusView }: ContentProps) => {
  useResetModuleWrapperStyles();

  const { clientId, accountId } = useContext(StatusViewsContext);

  const { isLoading, isError, refetch, data } = useDraftAccountQuery({
    clientId,
    draftId: accountId,
  });

  const [confirmationModal, setConfirmationModal] = useState<'approve' | 'reject' | null>(null);

  if (isLoading) {
    return (
      <Wrapper>
        <CenteredContainer>
          <Loading text="Loading..." />
        </CenteredContainer>
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper>
        <CenteredContainer>
          <ErrorState onAction={refetch} />
        </CenteredContainer>
      </Wrapper>
    );
  }

  if (!data) {
    throw new Error('No data for Apex Advisor Review');
  }

  const { data: draftAccount } = data;

  const fields = draftAccountToFormFields(draftAccount as any);

  if (!fields) {
    throw new Error('No fields for Apex Advisor Review');
  }

  const {
    primaryApplicant: {
      legalFirstName,
      legalLastName,
      streetAddressLine1,
      city,
      state,
      postalCode,
      dob,
      ssn,
      phoneNumber,
    },
    annualIncome,
    brokerDealerfirmName,
    companySymbols,
    eSigned,
    hasInterestedParty,
    interestedPartyFirstName,
    investmentExperience,
    investmentObjective,
    isAffiliatedExchangeOrFINRA,
    isControlPerson,
    isCustodianEmployee,
    isPoliticallyExposed,
    liquidityNeeds,
    liquidNetWorth,
    riskTolerance,
    snapId,
    timeHorizon,
    totalNetWorth,
    hasBeneficiary,
    retirementAccountBeneficiaryInfo,
  } = fields;

  return (
    <>
      <Box pt={56}>
        <Content>
          <Card maxWidth={640} mx="auto">
            <Text fontWeight={600} fontSize="h5" color="neutral.600" px="32" pt="32" pb="16">
              Please review the account information below
            </Text>

            <Box as="dl">
              <Field>
                <Label>Name</Label>
                <Value>
                  {legalFirstName} {legalLastName}
                </Value>
              </Field>

              <Field>
                <Label>Primary Address</Label>
                <Value>{`${streetAddressLine1}, ${city}, ${state &&
                  state.value} ${postalCode}`}</Value>
              </Field>

              <Field>
                <Label>Date of Birth</Label>
                <Value>{dob}</Value>
              </Field>

              <Field>
                <Label>SSN</Label>
                <Value>{ssn}</Value>
              </Field>

              <Field>
                <Label>Phone Number</Label>
                <Value>{phoneNumber}</Value>
              </Field>

              <Field>
                <Label>Estimated Annual Income</Label>
                <Value>{annualIncome.label}</Value>
              </Field>

              <Field>
                <Label>Estimated Liquid Net Worth</Label>
                <Value>{liquidNetWorth.label}</Value>
              </Field>

              <Field>
                <Label>Estimated Total Net Worth</Label>
                <Value>{totalNetWorth.label}</Value>
              </Field>

              <Field>
                <Label>
                  Are you or a family member a senior executive or 10% shareholder at a publicly
                  traded company?
                </Label>
                <Value>{isControlPerson.label}</Value>
              </Field>

              {isControlPerson && isControlPerson.value && (
                <Field>
                  <Label>Please list the publicly traded companies</Label>
                  <Value>{companySymbols}</Value>
                </Field>
              )}

              <Field>
                <Label>
                  Is the account holder an employee of, or affiliated with, Apex Fintech Solutions?
                </Label>
                <Value>{isCustodianEmployee.label}</Value>
              </Field>

              {!(isCustodianEmployee && isCustodianEmployee.value) && (
                <>
                  <Field>
                    <Label>
                      Are you or a family member employed by a Brokerage Firm, Securities Exchange,
                      or FINRA?
                    </Label>
                    <Value>{isAffiliatedExchangeOrFINRA.label}</Value>
                  </Field>

                  {isAffiliatedExchangeOrFINRA && isAffiliatedExchangeOrFINRA.value && (
                    <>
                      <Field>
                        <Label>
                          Please provide the name of the FINRA member firm or exchange you are
                          affiliated with.
                        </Label>
                        <Value>{brokerDealerfirmName}</Value>
                      </Field>
                      <Field>
                        <Label>407 Letter</Label>
                        <Value>
                          <Letter407 clientId={clientId} accountId={accountId} snapId={snapId} />
                        </Value>
                      </Field>
                    </>
                  )}
                </>
              )}

              <Field>
                <Label>
                  Are you or a member of your family a current or former high-level elected or
                  appointed public official?
                </Label>
                <Value>{isPoliticallyExposed ? isPoliticallyExposed.label : ''}</Value>
              </Field>

              <Field>
                <Label>How would you classify your liquidity needs?</Label>
                <Value>{liquidityNeeds ? liquidityNeeds.label : ''}</Value>
              </Field>

              <Field>
                <Label>What is your time horizon for your investments?</Label>
                <Value>{timeHorizon ? timeHorizon.label : ''}</Value>
              </Field>

              <Field>
                <Label>What is your risk tolerance?</Label>
                <Value>{riskTolerance ? riskTolerance.label : ''}</Value>
              </Field>

              <Field>
                <Label>What is your investment experience?</Label>
                <Value>{investmentExperience ? investmentExperience.label : ''}</Value>
              </Field>

              <Field>
                <Label>What is your investment objective?</Label>
                <Value>{investmentObjective ? investmentObjective.label : ''}</Value>
              </Field>

              <Field>
                <Label>Would you like to add an interested party?</Label>
                <Value>{hasInterestedParty.label}</Value>
              </Field>

              {hasInterestedParty.value === YES_NO_OPTIONS[0].value && (
                <Field>
                  <Label>Legal Name</Label>
                  <Value>{interestedPartyFirstName}</Value>
                </Field>
              )}

              <Field>
                <Label>
                  Do you acknowledge that you’re agreeing to our terms with an E-signature that is
                  legal and equivalent of your manual signature?
                </Label>
                <Value>{eSigned ? eSigned.label : ''}</Value>
              </Field>

              {hasBeneficiary.value === YES_NO_OPTIONS[0].value && (
                <Field>
                  <Label>Beneficiary Name</Label>
                  <Value>{`${retirementAccountBeneficiaryInfo.givenName} ${retirementAccountBeneficiaryInfo.familyName}`}</Value>
                </Field>
              )}
            </Box>
          </Card>
        </Content>

        <StickyFooter>
          <Button
            variant="secondary"
            size="large"
            onClick={() => setConfirmationModal('reject')}
            ml="auto"
            mr="16"
          >
            Reject
          </Button>

          <Button size="large" onClick={() => setConfirmationModal('approve')}>
            Approve
          </Button>
        </StickyFooter>
      </Box>

      {confirmationModal && (
        <ConfirmationModal
          type={confirmationModal}
          onClose={() => setConfirmationModal(null)}
          setStatusView={setStatusView}
        />
      )}
    </>
  );
};
